import React, { type MouseEvent, type ReactElement, memo } from 'react';
import { InView } from 'react-intersection-observer';

import { PrimaryButton } from '@components/Button/variants';
import LogoTITextEvent from '@components/Images/Rebranding/LogoTITextEvent';
import { articleContinuesBelowClasses } from '@components/RelatedStories/MidArticle';
import { useEventTracking } from '@hooks';
import { event_path } from '@routes';
import { ClickTarget, ImpressionElement, TrackingLocation, click, impression } from '@tracking';

import { MidArticleEventProps } from './interfaces';

const MidArticleEvent = ({ event }: MidArticleEventProps): ReactElement => {
  const { Track, trackEvent } = useEventTracking({
    location: TrackingLocation.RelatedEvent,
  });

  const { buttonText, date, id, location, name, slug } = event;

  const handleEventClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    trackEvent(
      click({
        clickTarget: ClickTarget.RelatedEvent,
        clickText: buttonText,
        contentId: +id,
      })
    );
  };

  const handleViewChange = (inView: boolean) => {
    if (inView) {
      trackEvent(impression({ impressionElement: ImpressionElement.RelatedEvent, context: id }));
    }
  };

  return (
    <Track>
      <aside
        className={`relative mb-48 mt-64 w-full sm:float-right sm:ml-48 sm:mt-16 sm:max-w-[434px] md:-mr-[232px] md:max-w-[464px] lg:-mr-[342px] lg:ml-56 lg:mt-24 lg:max-w-[550px] ${articleContinuesBelowClasses}`}
        data-testid={`mid-article-event-${id}`}
      >
        <InView
          className="relative -ml-16 w-[calc(100%_+_32px)] overflow-hidden bg-grayscale-beige px-16 py-20 sm:ml-0 sm:w-full sm:p-28 dark:bg-black"
          threshold={0.5}
          triggerOnce={true}
          onChange={handleViewChange}
        >
          <div className="flex w-full flex-col items-end overflow-hidden">
            <header className="relative w-full transform-gpu after:absolute after:right-0 after:top-0 after:h-px after:w-[200dvw] after:origin-top-right after:-rotate-[28deg] after:bg-primary-magenta after:content-[''] xs:after:-rotate-[16deg] sm:after:-rotate-[30deg] dark:after:bg-primary-magenta">
              <LogoTITextEvent
                aria-label="The Information Events"
                className="w-[162px] text-primary-magenta sm:w-[208px] lg:w-[234px] dark:[&>g>path]:fill-white"
              />
            </header>
            <div className="mt-64 max-w-[55dvw] text-right xs:mt-32 sm:mt-64 sm:max-w-[290px] lg:max-w-[320px]">
              <h4 className="m-0 text-balance font-suisse-works text-headline-sm sm:text-headline-lg dark:text-white">
                {name}
              </h4>
              <time className="text-caption-sm font-normal text-primary-magenta sm:text-caption-md dark:text-primary-pink">
                {date}
              </time>
              <span className="block text-caption-sm text-grayscale-gray">{location}</span>
            </div>
            <PrimaryButton
              as="link"
              className="mt-24 !no-underline"
              data-testid="mid-article-event-cta"
              href={event_path(slug)}
              onClickCapture={handleEventClick}
              target="_blank"
              fullWidth
            >
              {buttonText}
            </PrimaryButton>
          </div>
        </InView>
      </aside>
    </Track>
  );
};

export default memo(MidArticleEvent);
